import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";

export function ReviewPhotoUpload({
  value,
  onChange,
  max = 5,
}: {
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function uploadFiles(files: FileList | File[]) {
    const batch = [...files].slice(0, max - value.length);
    if (!batch.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of batch) formData.append("files", file);
      const res = await api<{ urls: string[] }>("/uploads/public", { formData });
      onChange([...value, ...res.urls].slice(0, max));
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((url, index) => (
            <div key={url} className="relative h-16 w-16 overflow-hidden rounded-lg border border-white/10">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-white/80"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                aria-label="Remove photo"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {value.length < max && (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 text-[11px] font-medium text-brand-pink disabled:opacity-50"
        >
          <Upload size={14} />
          {uploading ? "Uploading…" : `Add photos (${value.length}/${max})`}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void uploadFiles(e.target.files);
        }}
      />
    </div>
  );
}
